import React from 'react';
import I18n from 'i18n-js';
import {
  RCButton,
  RCText,
  RCAlertBox,
  RCModalConfirmation,
} from '@reverbdotcom/cadence/components';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { ErrorList } from '@reverbdotcom/commons/src/ErrorList';

const PAYPAL_ONBOARDING_HELP_ARTICLE_ID = '360017749574';

interface Props {
  partnerOnboarded: boolean;
  onboardingPending: boolean;
  hasPaypalProfile: boolean;
  userLockedForPaypalNoncompliance: boolean;
  partnerReferralTrackingId: string;
  listingId?: string;
}

export default function PaypalOnboardingButton(props: Props) {
  const {
    partnerOnboarded,
    onboardingPending,
    hasPaypalProfile,
    userLockedForPaypalNoncompliance,
    partnerReferralTrackingId,
    listingId,
  } = props;
  const [onboardingRequest, { errors: onboardErrors, loading: onboardingLoading }] = useMutation(PAYPAL_ONBOARD_SELLER_MUTATION);
  const [cancelPaypalOnboarding, { errors, loading: cancelLoading }] = useMutation(CANCEL_PAYPAL_ONBOARDING_MUTATION);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const helpCenterArticleUrl = useHelpCenterArticleUrl(PAYPAL_ONBOARDING_HELP_ARTICLE_ID);

  function buttonText() {
    if (partnerOnboarded) {
      return I18n.t('discovery.paypalOnboarding.changeEmail');
    } else if (onboardingPending) {
      return I18n.t('discovery.paypalOnboarding.connectionPending');
    } else {
      return I18n.t('discovery.paypalOnboarding.connect');
    }
  }

  function descriptionText() {
    if (partnerOnboarded) {
      return I18n.t('discovery.paypalOnboarding.successDescription');
    } else if (onboardingPending) {
      return I18n.t('discovery.paypalOnboarding.pendingDescription');
    }
  }

  function paypalAlertTextKey() {
    if (onboardingPending) {
      return 'discovery.paypalOnboarding.stuckAlert';
    }

    if (userLockedForPaypalNoncompliance) {
      return 'discovery.paypalOnboarding.lockedAlert';
    }

    return 'discovery.paypalOnboarding.alert';
  }

  const showAlert = hasPaypalProfile && !partnerOnboarded;

  const showCancel = onboardingPending || partnerOnboarded;

  const cancelTextKey = onboardingPending ? 'discovery.paypalOnboarding.cancel' : 'discovery.paypalOnboarding.disconnect';

  async function submitCancel() {
    const result = await cancelPaypalOnboarding({
      variables: {
        input: {
          partnerReferralTrackingId,
        },
      },
    });

    if (result.data?.cancelPaypalOnboarding?._id) {
      window.location.reload();
    }

    setIsModalOpen(false);
  }

  async function getOnboardingLink() {
    const result = await onboardingRequest({
      variables: {
        input: {
          listingId,
        },
      },
    });

    if (result.data?.onboardPaypalSeller?.actionUrl) {
      window.location.assign(result.data.onboardPaypalSeller?.actionUrl);
    }
  }

  const allErrors = [...onboardErrors, ...errors];

  function onClickCancel() {
    if (onboardingPending) {
      submitCancel();
    } else if (partnerOnboarded) {
      setIsModalOpen(true);
    }
  }

  return (
    <>
      <div className="d-flex fx-align-center gap-8">
        <RCButton
          onClick={getOnboardingLink}
          variant="filled"
          disabled={onboardingLoading || onboardingPending}
        >
          {buttonText()}
        </RCButton>
        {showCancel && (
          <>
            <RCButton
              onClick={onClickCancel}
              variant={onboardingPending ? 'transparent' : 'danger-muted'}
            >
              <Translate text={cancelTextKey} />
            </RCButton>
            <RCModalConfirmation
              actions={{
                primary: {
                  buttonText: I18n.t('discovery.paypalOnboarding.disconnect'),
                  onClick: () => submitCancel(),
                  preventCloseOnClick: true,
                  disabled: cancelLoading,
                  variant: onboardingPending ? 'filled' : 'danger',
                },
              }}
              isOpen={isModalOpen}
              onOpenChange={isOpen => {
                if (!isOpen) setIsModalOpen(false);
              }}
              title={I18n.t('discovery.paypalOnboarding.disconnectTitle')}
            >
              <Translate
                html
                text={'discovery.paypalOnboarding.disconnectDescription'}
              />
            </RCModalConfirmation>
          </>
        )}
      </div>
      {allErrors.length > 0 && (
        <div className="mt-2 mb-2">
          <RCAlertBox type="error" small>
            <ErrorList errors={allErrors} />
          </RCAlertBox>
        </div>
      )}
      {(onboardingPending || partnerOnboarded) &&
        <div className="mt-2">
          <RCText.Fine>
            {descriptionText()}
          </RCText.Fine>
        </div>
      }
      {showAlert &&
        <div className="mt-2">
          <RCAlertBox
            type="info"
          >
            <Translate
              html
              text={paypalAlertTextKey()}
              args={onboardingPending ? {} : {
                linkOpen: `<a target="_blank" href="${helpCenterArticleUrl}">`,
                linkClose: '</a>',
              }}
            />
          </RCAlertBox>
        </div>
      }
    </>
  );
}

export const PAYPAL_ONBOARD_SELLER_MUTATION = gql(`
  mutation Core_OnboardPaypalSeller($input: OnboardPaypalSellerInput!) {
    onboardPaypalSeller(input: $input) {
      actionUrl
    }
  }
`);

export const CANCEL_PAYPAL_ONBOARDING_MUTATION = gql(`
  mutation Core_CancelPaypalOnboarding($input: CancelPaypalOnboardingInput!) {
    cancelPaypalOnboarding(input: $input) {
      _id
    }
  }
`);
